import React, { useContext, useMemo, Fragment, useCallback, useState } from 'react';
import { Checkbox, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { EditRounded as EditIcon, ErrorRounded as ErrorRoundedIcon } from '@material-ui/icons';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  FLAG_INPUT, PATIENT, PRODUCT_INFO, FLAG_CHECKBOX_NOT_EDITABLE_MESSAGE,
  RESPONSE_POPUP_KEY, FLAG_PERMISSION_ERROR_MESSAGE, TRUE, TAB_DETAILS_SCREEN_RESIZE_DELAY,
  PRODUCTS_TABS_WRAPPER_SELECTOR, VIDEO_VISIT_WRAPPER_SELECTOR, FLAGS_COLUMN_SELECTOR,
  PAGE_HEADING_SELECTOR,
  ORDER_DETAILS_SELECTOR,
} from '../../../constants/patientChart.constant';
import PPButton, { VARIENT } from '../../subComponents/button';
import Input from '../../subComponents/input';
import { PopupV1 as Popup, POPUP_TYPE } from '../../subComponents/popup';
import useInlineHeight from '../../../hooks/useTabDetailsInlineHeight';
import { FEATURE } from '../../../utils/feature';
import { pickSecretAutoCompleteHash } from '../../../utils/random';
import {
  ORDER_FLAG, ORDER_AND_PRESCRIBER_FLAGS, SAVED_FLAGS, ORDERS,
  FLAG_TYPE
} from '../../../constants/visits.constant';
import { removeXSSAttacks } from '../../../common/helper';

const hasFeature = FEATURE();

const FlagWarningIconColorCollection = new Map([
  [true, 'color-red-v1'],
  [false, 'color-orange-v1'],
]);

const FlagWarningFontColorCollection = new Map([
  [true, 'color-red-v1'],
  [false, ''],
]);

const FlagWarning = ({
  flagText = '',
  required = false,
}) => {
  return (
    <div className='flex align-items-flex-start'>
      <div className='flex min-width-30'>
        <ErrorRoundedIcon className={FlagWarningIconColorCollection.get(required)} />
      </div>
      <div
        className={`flex ${FlagWarningFontColorCollection.get(required)}`}
        dangerouslySetInnerHTML={{
          __html: removeXSSAttacks(flagText),
        }}
      />
    </div>
  );
};

const FlagWarnings = () => {
  const {
    criticalFlags,
  } = useContext(PatientChartContext);

  const ComponentCollection = useMemo(() => {
    return new Map([
      ['true-true', () => (
        <div className='flex flex-column gap-16 m-b-6'>
          {
            criticalFlags.map(({ flagId, flagText, required }) => (
              <FlagWarning
                key={flagId}
                flagId={flagId}
                flagText={flagText}
                required={required}
              />
            ))
          }
        </div>
      )],
      ['true-false', () => <Fragment />],
      ['false-true', () => <Fragment />],
      ['false-false', () => <Fragment />],
    ]);
  }, [criticalFlags]);

  const Component = ComponentCollection.get(`${hasFeature.FLAGS_UPDATE}-${criticalFlags.length > 0}`);

  return (
    <Component />
  );
};

const FlagInput = ({
  orderId = 0,
  productId = 0,
  questionId = 0,
  flagId = 0,
  flagText = '',
  flagType = '',
  flagResponseId = '',
  orders = ORDERS(),
  isAssignedDoctorViewing = false,
}) => {
  const { flag: { current: flag, prev: _flag, }, setFlag } = useContext(PatientChartContext);

  const [readOnly, setReadOnly] = useState(true);

  const pickFlagInput = ({ flagResponseId = '' }) => {
    const input = new Map(flag.input);

    if (!input.has(flagResponseId)) {
      input.set(flagResponseId, { ...FLAG_INPUT(), responseId: flagResponseId });
    }

    const flagInput = input.get(flagResponseId);
    return { input, flagInput };
  };

  const handleCheck = ({ flagResponseId = '' }, _, checked = false) => {
    const { input, flagInput } = pickFlagInput({ flagResponseId });

    if (flagInput.note.length > 0 && !checked) {
      return {};
    }

    flagInput.checked = checked;

    input.set(flagResponseId, flagInput);
    setFlag({
      editMode: true,
      prev: _flag,
      current: { ...flag, input },
    });
    return {};
  };

  const handleNoteChange = ({ flagResponseId = '' }, { target: { value = '' } }) => {
    const { input, flagInput } = pickFlagInput({ flagResponseId });
    flagInput.orderId = orderId;
    flagInput.productId = productId;
    flagInput.questionId = questionId;
    flagInput.flagId = flagId;
    // flagInput.acknowledgment = true;
    flagInput.checked = value.length > 0;
    // flagInput.flagText = value;
    flagInput.note = value;
    flagInput.flagType = flagType;
    flagInput.responseId = flagResponseId;

    input.set(flagResponseId, flagInput);
    setFlag({
      editMode: true,
      prev: _flag,
      current: { ...flag, input },
    });
  };

  const { checked, note } = useMemo(() => {
    if (!flag.input.has(flagResponseId)) {
      return FLAG_INPUT();
    }

    const flagInput = flag.input.get(flagResponseId);
    return flagInput;
  }, [flag]);

  // eslint-disable-next-line no-unused-vars
  const disabled = useMemo(() => {
    if (!isAssignedDoctorViewing) {
      return true;
    }

    if (!orders.all.collection.has(orderId)) {
      return true;
    }
    return false;
  }, [orders, isAssignedDoctorViewing]);

  const handleFocus = useCallback(() => {
    setReadOnly(false);
  }, []);

  const handleBlur = useCallback(() => {
    setReadOnly(true);
  }, []);

  const NoteInput = new Map([
    [true, (
      <Input
        name={`note-for-assigned-doctor-${pickSecretAutoCompleteHash(4)}-hash-${pickSecretAutoCompleteHash(4)}`}
        type='text'
        className='flex flex-fill'
        fontSize='16'
        max={255}
        value={note}
        readOnly={readOnly}
        onChange={handleNoteChange.bind(this, { flagResponseId })}
        placeholder='Notes'
        autoComplete={`nope-note-for-assigned-doctor-${pickSecretAutoCompleteHash(4)}-hash-${pickSecretAutoCompleteHash(4)}`}
        message={note.length === 0 ? 'Required' : ''}
        error={note.length === 0}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    )],
    [false, (
      <Tooltip title={FLAG_PERMISSION_ERROR_MESSAGE} placement='top' arrow>
        <div className='flex flex-fill'>
          <Input
            name={`note-for-assigned-doctor-${pickSecretAutoCompleteHash(4)}-hash-${pickSecretAutoCompleteHash(4)}`}
            type='text'
            className='flex flex-fill'
            fontSize='16'
            max={255}
            value={note}
            disabled={true}
            onChange={() => {}}
            placeholder='Notes'
            autoComplete={`nope-note-for-unassigned-doctor-${pickSecretAutoCompleteHash(4)}-hash-${pickSecretAutoCompleteHash(4)}`}
          />
        </div>
      </Tooltip>
    )],
  ]);

  const enabledCheckbox = (
    <Checkbox
      title='flag-selection'
      className='checkbox'
      inputProps={{ 'aria-label': 'primary checkbox' }}
      checked={checked}
      onChange={handleCheck.bind(this, { flagResponseId })}
    />
  );

  const disabledCheckbox = (
    <Tooltip title={FLAG_PERMISSION_ERROR_MESSAGE} placement='top' arrow>
      <div className='flex flex-fill'>
        <Checkbox
          title='flag-selection'
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={checked}
          disabled={true}
          onChange={() => {}}
        />
      </div>
    </Tooltip>
  );

  const NoteCheckbox = new Map([
    ['true-false', enabledCheckbox],
    ['true-true', (
      <Tooltip title={FLAG_CHECKBOX_NOT_EDITABLE_MESSAGE} placement='top' arrow>
        <div className='flex flex-fill'>
          {enabledCheckbox}
        </div>
      </Tooltip>
    )],
    ['false-true', disabledCheckbox],
    ['false-false', disabledCheckbox],
  ]);

  return (
    <Fragment>
      <div className='flex align-items-flex-start m-b-1'>
        <div className='flex min-width-30'>
          {NoteCheckbox.get(`${isAssignedDoctorViewing}-${note.length > 0}`)}
        </div>
        <div className='flex'>{flagText}</div>
      </div>
      <div className='flex align-items-center m-b-6'>
        {NoteInput.get(isAssignedDoctorViewing)}
      </div>
    </Fragment>
  );
};

const ProductsWithFlags = ({
  orderId = 0,
  productId = 0,
  flagsCollection = new Map([
    [0, [ORDER_FLAG()]],
  ]),
  product = PRODUCT_INFO(),
  savedFlags = SAVED_FLAGS(),
  orders = ORDERS(),
  isAssignedDoctorViewing = false,
}) => {
  const _product = product.productMap.get(productId);
  const _flags = flagsCollection.get(productId);

  return (
    <>
      <div className='flex p-t-1 p-b-1 p-r-1 m-b-2 bg-lighter-grey border-bottom-2-bg-dark-primary-v4'>
        <div className='min-width-30'></div>
        <div className='font-16 font-roboto bold'>{_product?.displayTitle}</div>
      </div>

      <div className='flex flex-column'>
        {
          _flags.map(({ flagId, questionId, flagText }) => (
            <FlagInput
              key={questionId}
              flagResponseId={`${productId}-${flagId}`}
              flagText={flagText}
              savedFlags={savedFlags}
              orderId={orderId}
              productId={productId}
              questionId={questionId}
              flagId={flagId}
              flagType={FLAG_TYPE.PRESCRIBER}
              orders={orders}
              isAssignedDoctorViewing={isAssignedDoctorViewing}
            />
          ))
        }
      </div>
    </>
  );
};

export const Flags = ({
  orderId = 0,
  patientId = 0,
  doctorId = 0,
  patient = PATIENT(),
  product = PRODUCT_INFO(),
  flags = ORDER_AND_PRESCRIBER_FLAGS(),
  savedFlags = SAVED_FLAGS(),
  saveFlagsInfo = {},
  orders = ORDERS(),
  scheduledVisits = {},
  asyncOrders = {},

  saveFlags = async (requestBody = []) => {},
  getSavedFlags = async (requestBody = []) => {},
  getNotesTabInfo = async () => {},
}) => {
  const {
    flag: { editMode, current: flag, prev: _flag },
    setFlag,
    orderFlags,
    prescriberFlags,
    isAssignedDoctorViewing,
    areFlagsValid,
    noOfRequiredFlags,
    ModalCollection,
    handleModalOpen,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);

  const { api: { loading, called } } = flags;

  useInlineHeight({
    TRUE,
    delay: TAB_DETAILS_SCREEN_RESIZE_DELAY,
    selector: {
      targetElement: FLAGS_COLUMN_SELECTOR,
    },
    maxCounter: 6000, // checks for ui changes for 30 mins
    selectors: [
      PAGE_HEADING_SELECTOR,
      ORDER_DETAILS_SELECTOR,
      PRODUCTS_TABS_WRAPPER_SELECTOR,
      VIDEO_VISIT_WRAPPER_SELECTOR,
    ],
  });

  const { productIds, productIdsSet, flagsCollection } = useMemo(() => {
    const productIdsSet = new Set([0]);
    const flagsCollection = new Map([
      [0, [ORDER_FLAG()]],
    ]);

    productIdsSet.delete(0);
    flagsCollection.delete(0);

    prescriberFlags.forEach(({ flagId, questionId, flagText, flagType, productId, flaggedQuestion, flaggedAnswer }) => {
      productIdsSet.add(productId);

      if (!flagsCollection.has(productId)) {
        flagsCollection.set(productId, []);
      }

      const _flags = flagsCollection.get(productId);
      _flags.push({ flagId, questionId, flagText, flagType, productId, flaggedQuestion, flaggedAnswer });
    });

    const _productIds = [...productIdsSet];
    const _productMap = product.productMap;
    const productIds = [];

    for (let i = 0; i < _productIds.length; i++) {
      const pId = _productIds[i];
      if (_productMap.has(pId)) {
        productIds.push(pId);
      }
    }

    return { productIds, productIdsSet, flagsCollection };
  }, [prescriberFlags]);

  const { questionsCollection } = useMemo(() => {
    const questionsCollection = new Map([['', {
      flagType: '',
      flagText: '',
      flaggedQuestion: '',
      flaggedAnswer: '',
    }]]);
    questionsCollection.delete('');

    orderFlags.forEach(({ flagId, flagText, flagType, flaggedAnswer, flaggedQuestion }) => {
      questionsCollection.set(`0-${flagId}`, {
        flagType,
        flagText,
        flaggedQuestion,
        flaggedAnswer,
      });
    });

    prescriberFlags.forEach(({ flagId, productId, flagText, flagType, flaggedQuestion, flaggedAnswer }) => {
      questionsCollection.set(`${productId}-${flagId}`, {
        flagType,
        flagText,
        flaggedQuestion,
        flaggedAnswer,
      });
    });

    return { questionsCollection };
  }, [orderFlags, prescriberFlags]);

  const LoadingTemplate = (
    <div className='flex flex-column'>
      <div className='flex m-b-2'>
        <Skeleton variant='text' className='flex flex-fill' />
      </div>
      
      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex min-width-30 m-r-2' />
        <Skeleton variant='text' className='flex flex-fill' />
      </div>

      <div className='flex m-b-3'>
        <Skeleton variant='text' className='flex min-width-30 m-r-2' />
        <Skeleton variant='text' className='flex flex-fill' />
      </div>
    </div>
  );

  const handleEdit = () => {
    setFlag({ editMode: true, current: flag, prev: _flag });
  };

  const handleSaveNotes = async () => {
    const requestBody = [...flag.input.values()].map(({ updatedBy, updatedAt, note, checked, required, responseId, ...input }) => {
      let flagText = FLAG_TYPE.ORDER;
      let flagQuestion = '';
      let flaggedQuestion = '';
      let flaggedAnswer = '';

      if (questionsCollection.has(responseId)) {
        const qAndA = questionsCollection.get(responseId);
        flagText = qAndA.flagText;
        flaggedQuestion = qAndA.flaggedQuestion;
        flaggedAnswer = qAndA.flaggedAnswer;

        const _flagQuestionMapper = new Map([
          [FLAG_TYPE.ORDER, qAndA.flagText],
          [FLAG_TYPE.PRESCRIBER, qAndA.flaggedQuestion],
        ]);

        flagQuestion = _flagQuestionMapper.get(qAndA.flagType);
      }

      return {
        ...input,
        patientId,
        flagQuestion,
        flaggedQuestion,
        flaggedAnswer,
        flagTextV1: flagText,
        flagText: note,
        note,
        acknowledgment: checked,
      };
    });

    try {
      const { status, data } = await saveFlags(requestBody);
      if (status === 200) {
        getSavedFlags({ OrderId: orderId });
        getNotesTabInfo();
      }

      if (status !== 200) {
        const { Error: error = 'Failed to save the flags. Please try later.' } = data;
        let heading = 'Error';

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.FLAGS, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {

    }
  };

  const isSaveDisabled = useMemo(() => {
    if (!areFlagsValid) {
      return true;
    }

    if (!called) {
      return true;
    }

    if (loading) {
      return true;
    }

    return false;
  }, [areFlagsValid, loading, called]);

  const isEditDisabled = useMemo(() => {
    if (!isAssignedDoctorViewing) {
      return true;
    }

    if (noOfRequiredFlags === 0) {
      return true;
    }

    return false;
  }, [isAssignedDoctorViewing, noOfRequiredFlags]);

  const isCalled = useMemo(() => {
    if (called) {
      return true;
    }

    if (product.api.called) {
      return true;
    }

    return false;
  }, [called, scheduledVisits.visits, asyncOrders.visits, product.api]);

  const isLoading = useMemo(() => {
    if (loading) {
      return true;
    }

    if (product.api.loading) {
      return true;
    }

    return false;
  }, [loading, scheduledVisits.visits, asyncOrders.visits, product.api, orders, orderId]);

  const responseModal = useMemo(() => {
    if (!ModalCollection.has(RESPONSE_POPUP_KEY.FLAGS)) {
      return { heading: '', type: '', message: '' };
    }
    return ModalCollection.get(RESPONSE_POPUP_KEY.FLAGS);
  }, [ModalCollection]);

  const FlagAction = new Map([
    [true, (
      <PPButton
        label='Save Notes'
        disabled={isSaveDisabled}
        onClick={handleSaveNotes}
        loading={saveFlagsInfo.api.loading || savedFlags.api.loading}
      />
    )],
    [false, (
      <PPButton
        label={<EditIcon />}
        disabled={isEditDisabled}
        onClick={handleEdit}
        loading={saveFlagsInfo.api.loading || savedFlags.api.loading}
        variant={VARIENT.outlined}
      />
    )],
  ]);

  const FlagsTemplate = new Map([
    ['false-false', LoadingTemplate],
    ['false-true', LoadingTemplate],
    ['true-true', LoadingTemplate],
    ['true-false', (
      <Fragment>
        <FlagWarnings />

        {
          (orderFlags.length + prescriberFlags.length) === 0 && (
            <div className='flex align-items-flex-start m-b-6'>
              <div className='flex'>No flag found for this order!</div>
            </div>
          )
        }

        {
          orderFlags.map(({ flagId, flagText }) => (
            <FlagInput
              key={flagId}
              flagResponseId={`0-${flagId}`}
              flagText={flagText}
              savedFlags={savedFlags}
              orderId={orderId}
              productId={0}
              questionId={0}
              flagId={flagId}
              flagType={FLAG_TYPE.ORDER}
              orders={orders}
              isAssignedDoctorViewing={isAssignedDoctorViewing}
            />
          ))
        }

        {
          productIds.map((productId) => (
            <ProductsWithFlags
              key={productId}
              orderId={orderId}
              patientId={patientId}
              doctorId={doctorId}
              productId={productId}
              productIdsSet={productIdsSet}
              flagsCollection={flagsCollection}
              product={product}
              patient={patient}
              savedFlags={savedFlags}
              orders={orders}
              isAssignedDoctorViewing={isAssignedDoctorViewing}
            />
          ))
        }

        {FlagAction.get(editMode)}

        <Popup open={ModalCollection.has(RESPONSE_POPUP_KEY.FLAGS)} title={responseModal.heading} type={POPUP_TYPE.ERROR}>
          <div className='flex flex-column'>
            <div className='flex align-items-center m-b-5 font-roboto'>{responseModal.message}</div>
            <div className='flex align-items-center justify-content-center'>
              <PPButton
                label='Close'
                variant='contained'
                endGutter
                onClick={handleMultipleModalClose.bind(this, { keys: [RESPONSE_POPUP_KEY.FLAGS] })}
              />
            </div>
          </div>
        </Popup>
      </Fragment>
    )]
  ]);

  return FlagsTemplate.get(`${isCalled}-${isLoading}`);
};

export default Flags;
